import { useMemo } from "react";
import { Text } from "@volca/ui/components/ui/text";
import { useReportCurrency } from "@/profitability/currency";
import { useRowDetail } from "@/profitability/interactive-report/row-detail-context";
import { isBlank } from "@/profitability/interactive-report/utils";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type {
  ReportBlock,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import { datasetColumns, rowKeyOf } from "./dataset-columns";
import { ObjectTable } from "./object-table";
import {
  findRecord,
  flagColumn,
  recordId,
  recordName,
  type RecordRef,
} from "./objects";
import {
  listsFindings,
  useRecordObjects,
  useReviewScope,
} from "./objects-context";
import { RunFindingsBlock } from "./run-findings-block";

/**
 * A report's table of records, as the record table: its row opens the record
 * in the report's sidebar, and a linked record opens that one.
 */
export const ObjectTableBlock = ({
  block,
  dataset,
  rows,
  headline,
  label,
}: {
  block: Extract<ReportBlock, { type: "table" }>;
  dataset: ReportDataset;
  /** The rows the block shows, after the pane's filters (and its flag). */
  rows: ReportRow[];
  headline?: string[];
  label: string;
}) => {
  const { columns: fields, detail, sort: defaultSort } = block;
  const scope = useReviewScope();
  const objects = useRecordObjects();
  const rowDetail = useRowDetail();
  const currency = useReportCurrency();
  const review = useReviewSpec();
  const object = objects.find((item) => item.dataset.id === dataset.id)!;
  const key = rowKeyOf(object);
  const open = rowDetail?.open;
  const recordIds = useMemo(
    () => rows.map((row) => recordId(dataset, row)),
    [dataset, rows],
  );
  const openRef = (ref: RecordRef) => {
    const target = objects.find((item) => item.dataset.id === ref.dataset);
    const row = target && findRecord(target.dataset, ref.recordId);
    if (target && row) rowDetail?.openRow({ row, dataset: target.dataset });
  };
  // A table of the flagged rows is the run's review once the run has its
  // findings: rated and closed where they are listed, limited to the rows
  // the block shows. A draft's sample never has findings, and a run still
  // being reviewed has none yet, so either lists its flagged rows as rows.
  if (block.flagged && listsFindings(scope)) {
    const flag = flagColumn(dataset);
    const flagged = flag
      ? dataset.rows.filter((row) => !isBlank(row[flag.key])).length
      : rows.length;
    return (
      <RunFindingsBlock
        scope={scope!}
        objects={objects}
        dataset={dataset.id}
        recordIds={recordIds}
        filtered={rows.length < flagged}
        noun={object.plural.toLowerCase()}
        selected={
          open
            ? {
                dataset: open.dataset.id,
                recordId: recordId(open.dataset, open.row),
              }
            : null
        }
        onOpenRecord={openRef}
      />
    );
  }
  return (
    <>
      {block.flagged && scope?.reviewing && scope.reviewsFindings && (
        <Text variant="caption" tone="muted" role="status" className="px-4">
          Reviewing… Flagged {object.plural.toLowerCase()} become findings here
          once Volca has looked at them.
        </Text>
      )}
      <ObjectTable
        label={label}
        className="max-h-[36rem]"
        fill
        rows={rows}
        columns={datasetColumns({
          object,
          objects,
          only: fields,
          currency,
          review,
          onOpenRecord: openRef,
        })}
        rowKey={key}
        rowLabel={(row) => `${object.name} ${recordName(object, row)}`}
        selectedKey={
          open && open.dataset.id === dataset.id ? key(open.row) : null
        }
        onOpenRow={
          rowDetail
            ? (row, shown) =>
                rowDetail.openRow({
                  row,
                  dataset,
                  detail,
                  headline,
                  list: {
                    label: object.plural,
                    refs: shown.map((item) => ({
                      dataset: dataset.id,
                      recordId: key(item),
                    })),
                  },
                })
            : undefined
        }
        countLabel={`${rows.length} ${(rows.length === 1 ? object.name : object.plural).toLowerCase()}`}
        defaultSort={
          defaultSort
            ? { column: defaultSort.by, direction: defaultSort.direction }
            : undefined
        }
      />
    </>
  );
};
